import type { ColorPalette } from './types'

/**
 * safeapp olive-green dark palette.
 *
 * Drop-in replacement for packages/theme/src/palettes/dark.ts in
 * safe-global/safe-wallet-monorepo. Same shape (ColorPalette), same roles:
 * `primary` is the brand color in dark mode. Bright olive #98A65A keeps
 * ~6.9:1 contrast (WCAG AA text) on the #14150F background; the hover step
 * (`primary.dark`) is the LIGHTER dark-mode --sa-olive-600, mirroring
 * theme/olive.css.
 *
 * Only brand / neutral / surface values changed; semantic status colors
 * (error, error1, success, info, warning, warning1, review) are verbatim
 * upstream. Hex values mirror theme/tokens.json in chews/safeapp — keep in
 * sync (see theme/safe-ui-overlay/README.md).
 */
const darkPalette: ColorPalette = {
  text: {
    primary: '#FFFFFF',
    secondary: '#6A6E60',
    disabled: 'rgba(255, 255, 255, 0.3)',
    contrast: '#14160F',
  },
  primary: {
    dark: '#ACB96E',
    main: '#98A65A',
    light: '#A6A99C',
  },
  secondary: {
    dark: '#6A6E60',
    main: '#FFFFFF',
    light: '#CBD79A',
    background: '#232619',
  },
  border: {
    main: '#6A6E60',
    light: '#30332B',
    background: '#14150F',
  },
  error: {
    dark: '#FFE0E6',
    main: '#FF5F72',
    light: '#4A2125',
    background: '#4A2125',
  },
  error1: {
    main: '#4A2125',
    contrastText: '#FFE0E6',
  },
  success: {
    dark: '#DEFDEA',
    main: '#00B460',
    light: '#3B7A54',
    background: '#173026',
  },
  info: {
    dark: '#D9F4FB',
    main: '#00BFE5',
    light: '#458898',
    background: '#203339',
  },
  warning: {
    dark: '#FFE4CB',
    main: '#FF8C00',
    light: '#A65F34',
    background: '#4A3621',
  },
  warning1: {
    main: '#4A3621',
    text: '#FFE4CB',
    contrastText: '#FF8C00',
  },
  review: {
    main: '#D6A100',
    background: '#2F2A18',
  },
  background: {
    default: '#14150F',
    main: '#14150F',
    sheet: '#14150F',
    paper: '#1D1E17',
    light: '#232619',
    secondary: '#30332B',
    skeleton: 'rgba(255, 255, 255, 0.04)',
    disabled: '#7878801F',
  },
  backdrop: {
    main: '#63665C',
  },
  logo: {
    main: '#FFFFFF',
    background: '#30332B',
  },
  static: {
    main: '#1A1D14',
    light: '#63665C',
    primary: '#FFFFFF',
    textSecondary: '#9A9E90',
    textBrand: '#98A65A',
  },
}

export default darkPalette
